import React, { useState, useEffect } from "react";
import styles from "../css/PaymentModal.module.css";
import { authAPI, userAPI } from "../services/Api";
import { FaMapMarkerAlt } from "react-icons/fa";
const states = [
    "Andhra Pradesh",
    "Arunachal Pradesh",
    "Assam",
    "Bihar",
    "Chhattisgarh",
    "Goa",
    "Gujarat",
    "Haryana",
    "Himachal Pradesh",
    "Jharkhand",
    "Karnataka",
    "Kerala",
    "Madhya Pradesh",
    "Maharashtra",
    "Manipur",
    "Meghalaya",
    "Mizoram",
    "Nagaland",
    "Odisha",
    "Punjab",
    "Rajasthan",
    "Sikkim",
    "Tamil Nadu",
    "Telangana",
    "Tripura",
    "Uttar Pradesh",
    "Uttarakhand",
    "West Bengal",
];

const PaymentModal = ({ isOpen, onClose, onPlaceOrder }) => {
    const [processing, setProcessing] = useState(false);
    const [success, setSuccess] = useState(false);
    const [address, setAddress] = useState({
        name: "",
        phone: "",
        email: "",
        address: "",
        addressLine2: "",
        landmark: "",
        city: "",
        state: "",
        pincode: "",
        latitude: null,
        longitude: null,
    });
    const [error, setError] = useState("");
    const [locationLoading, setLocationLoading] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        const loadSavedAddress = async () => {
            try {
                const res = await authAPI.getProfile();
                const u = res.user || {};
                setAddress(prev => ({
                    ...prev,
                    name: u.name || prev.name,
                    phone: u.phone || u.number || prev.phone,
                    email: u.email || prev.email,
                    address: u.address || prev.address,
                    addressLine2: u.addressLine2 || prev.addressLine2,
                    landmark: u.landmark || prev.landmark,
                    city: u.city || prev.city,
                    state: u.state || prev.state,
                    pincode: u.pincode || prev.pincode,
                    latitude: u.latitude || prev.latitude,
                    longitude: u.longitude || prev.longitude,
                }));
            } catch (e) {
                // ignore - user can fill manually
            }
        };
        loadSavedAddress();
    }, [isOpen]);

    const handleGetCurrentLocation = () => {
        if (!navigator.geolocation) {
            setError("Geolocation is not supported by this browser.");
            return;
        }

        setLocationLoading(true);
        setError("");

        navigator.geolocation.getCurrentPosition(
            (position) => {
                const { latitude, longitude } = position.coords;
                setAddress(prev => ({
                    ...prev,
                    latitude,
                    longitude,
                }));
                setLocationLoading(false);
                setError(""); // Clear any previous errors
            },
            (error) => {
                setLocationLoading(false);
                switch (error.code) {
                    case error.PERMISSION_DENIED:
                        setError("Location access denied. Please enable location permissions and try again.");
                        break;
                    case error.POSITION_UNAVAILABLE:
                        setError("Location information is unavailable. Please try again.");
                        break;
                    case error.TIMEOUT:
                        setError("Location request timed out. Please try again.");
                        break;
                    default:
                        setError("An unknown error occurred while retrieving location.");
                        break;
                }
            },
            {
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 300000, // 5 minutes
            }
        );
    };

    if (!isOpen) return null;

    const handlePay = async () => {
        const newErrors = {};

        if (!/^[a-zA-Z]+(?: [a-zA-Z]+)*$/.test(address.name.trim())) {
            newErrors.name = "Name can only contain letters and single spaces between words.";
        }
        if (!address.phone || address.phone.length < 10) {
            newErrors.phone = "Please enter a valid phone number with country code.";
        }
        if (!/^[a-zA-Z0-9.]+@gmail\.com$/.test(address.email)) {
            newErrors.email = "Please enter a valid Gmail address (e.g., example@gmail.com). No spaces or special characters allowed.";
        }
        if (!/^[a-zA-Z0-9\s,.-/]+$/.test(address.address.trim())) {
            newErrors.address = "Address can only contain letters, numbers, and basic symbols (, . - /).";
        }
        if (address.addressLine2.trim() && !/^[a-zA-Z0-9\s,.-/]+$/.test(address.addressLine2.trim())) {
            newErrors.addressLine2 = "Address Line 2 can only contain letters, numbers, and basic symbols (, . - /).";
        }
        if (address.landmark.trim() && !/^[a-zA-Z0-9\s,.-/]+$/.test(address.landmark.trim())) {
            newErrors.landmark = "Landmark can only contain letters, numbers, and basic symbols (, . - /).";
        }
        if (!/^[a-zA-Z0-9\s]+$/.test(address.city)) {
            newErrors.city = "City can only contain letters, numbers and spaces.";
        }
        if (!address.state) {
            newErrors.state = "Please select a state.";
        }
        if (!/^[0-9]{6}$/.test(address.pincode)) {
            newErrors.pincode = "Please enter a valid 6-digit pincode.";
        }

        if (Object.keys(newErrors).length > 0) {
            setError(Object.values(newErrors).join(" \n"));
            return;
        }

        if (
            !address.name ||
            !address.phone ||
            !address.email ||
            !address.address ||
            !address.city ||
            !address.pincode
        ) {
            setError("Please fill all address fields.");
            return;
        }

        setError("");
        setProcessing(true);
        try {
            await onPlaceOrder(
                address,
                () => {
                    setProcessing(false);
                    setSuccess(true);
                    // Save address to user profile for next time
                    userAPI.updateProfile({
                        address: address.address,
                        addressLine2: address.addressLine2,
                        landmark: address.landmark,
                        city: address.city,
                        state: address.state,
                        pincode: address.pincode,
                        latitude: address.latitude,
                        longitude: address.longitude,
                    }).catch(() => {});
                },
                (err) => {
                    setProcessing(false);
                    setError("Order failed. Try again.");
                }
            );
        } catch (e) {
            setProcessing(false);
            setError("Order failed. Try again.");
        }
    };

    const handleClose = () => {
        setSuccess(false);
        setProcessing(false);
        setAddress({
            name: "",
            phone: "",
            email: "",
            address: "",
            addressLine2: "",
            landmark: "",
            city: "",
            state: "",
            pincode: "",
            latitude: null,
            longitude: null,
        });
        setError("");
        onClose();
    };

    return (
        <div className={styles.overlay}>
            <div className={styles.modal}>
                <h2>Delivery Address</h2>
                {success ? (
                    <>
                        <p className={styles.successMessage}>
                            Order Placed Successfully!
                        </p>
                        <button
                            onClick={handleClose}
                            className={`${styles.btn} ${styles.primaryBtn}`}
                        >
                            Close
                        </button>
                    </>
                ) : (
                    <>
                        <div className={styles.formContainer}>
                            <input
                                type="text"
                                placeholder="Full Name"
                                value={address.name}
                                onChange={(e) =>
                                    setAddress({ ...address, name: e.target.value })
                                }
                                className={styles.input}
                                disabled={processing}
                            />
                            {/* Lightweight fallback phone input so app compiles without react-phone-input-2 */}
                            <div className={styles.phoneWrapper} style={{ marginBottom: 15 }}>
                                <select
                                    value={address.countryCode || "+91"}
                                    onChange={(e) => setAddress({ ...address, countryCode: e.target.value })}
                                    className={styles.countrySelect}
                                    disabled={processing}
                                >
                                    <option value="+91">+91</option>
                                    <option value="+1">+1</option>
                                    <option value="+44">+44</option>
                                </select>
                                <input
                                    type="tel"
                                    placeholder="Phone number"
                                    value={address.phone}
                                    onChange={(e) => setAddress({ ...address, phone: e.target.value.replace(/[^0-9]/g, '') })}
                                    className={`${styles.input} ${styles.phoneInput}`}
                                    disabled={processing}
                                />
                            </div>
                            <input
                                type="email"
                                placeholder="Email"
                                value={address.email}
                                onChange={(e) => setAddress({ ...address, email: e.target.value })}
                                className={styles.input}
                                style={{ marginTop: 0 }}
                                disabled={processing}
                            />
                            <textarea
                                placeholder="Delivery Address"
                                value={address.address}
                                onChange={(e) => setAddress({ ...address, address: e.target.value })}
                                className={styles.textareaa}
                                disabled={processing}

                            />
                            <textarea
                                placeholder="Address Line 2"
                                value={address.addressLine2}
                                onChange={(e) =>
                                    setAddress({ ...address, addressLine2: e.target.value })
                                }
                                className={styles.textareaa}
                                disabled={processing}
                            />
                            <input
                                type="text"
                                placeholder="Landmark"
                                value={address.landmark}
                                onChange={(e) =>
                                    setAddress({ ...address, landmark: e.target.value })
                                }
                                className={styles.input}
                                disabled={processing}
                            />

                            <select
                                className={`${styles.select} form-control`}
                                value={address.state}
                                onChange={(e) => setAddress({ ...address, state: e.target.value })}
                            >
                                <option value="">-- Select State --</option>
                                {states.map((state, index) => (
                                    <option key={index} value={state}>
                                        {state}
                                    </option>
                                ))}
                            </select>

                            <input
                                type="text"
                                placeholder="City"
                                value={address.city}
                                onChange={(e) => setAddress({ ...address, city: e.target.value })}
                                className={styles.input}
                                disabled={processing}
                            />
                            <input
                                type="text"
                                placeholder="Pincode"
                                value={address.pincode}
                                onChange={(e) => setAddress({ ...address, pincode: e.target.value })}
                                className={styles.input}
                                disabled={processing}
                            />

                            {/* Location Sharing Section */}
                            <div className={styles.locationSection}>
                                <button
                                    type="button"
                                    onClick={handleGetCurrentLocation}
                                    disabled={processing || locationLoading}
                                    className={`${styles.btn} ${styles.locationBtn}`}
                                >
                                    <FaMapMarkerAlt style={{ marginRight: '8px' }} />
                                    {locationLoading ? "Getting Location..." : "Share Current Location"}
                                </button>
                                {address.latitude && address.longitude && (
                                    <div className={styles.locationDisplay}>
                                        <small style={{ color: '#28a745', fontSize: '0.8rem' }}>
                                            ✓ Location shared: {address.latitude.toFixed(6)}, {address.longitude.toFixed(6)}
                                        </small>
                                    </div>
                                )}
                            </div>
                        </div>
                        {error && (
                            <div className={styles.error}>{error}</div>
                        )}
                        <button
                            onClick={handlePay}
                            disabled={processing}
                            className={`${styles.btn} ${styles.primaryBtn}`}
                        >
                            {processing ? "Placing Order..." : "Place Order"}
                        </button>
                        <br />
                        <button
                            onClick={handleClose}
                            disabled={processing}
                            className={`${styles.btn} ${styles.secondaryBtn}`}
                        >
                            Cancel
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};

export default PaymentModal;